import type {
  AdminUserDirectoryFilters,
  AdminUserPopulation,
} from '@shared/adminUsers';
import { EXAM_LEVELS } from '@shared/examLevels';
import { ORG_LEVELS } from '@shared/orgLevels';
import { ALL_ROLES, type RoleId, ROLES } from '@shared/roles';
import { validatedProductAnalyticsReturnTo } from '@/utils/productAnalyticsNavigation';

export const userDirectoryRoles = (population: AdminUserPopulation) =>
  population === 'nonprofit' ? [ROLES.USER, ROLES.TEACHER] : ALL_ROLES;

const oneOf = <T extends string>(
  value: string | null,
  values: readonly T[]
): T | undefined => (values.includes(value as T) ? (value as T) : undefined);
const id = (value: string | null) =>
  value && /^[a-f\d]{24}$/i.test(value) ? value : undefined;

export function parseUserDirectoryFilters(
  params: URLSearchParams,
  population: AdminUserPopulation,
  pinned: {
    cohortId?: string;
    organizationId?: string;
    groupId?: string;
    role?: RoleId;
  } = {}
): AdminUserDirectoryFilters {
  const page = Number(params.get('page'));
  const limit = Number(params.get('limit'));
  // The nonprofit directory now has one level/group picker. Older links used
  // the student-class projection for the same group; prefer the roster filter.
  const groupId =
    population === 'nonprofit'
      ? (pinned.groupId ??
        id(params.get('groupId')) ??
        id(params.get('classCodeId')))
      : undefined;
  return {
    population,
    sort:
      oneOf(params.get('sort'), ['email_asc', 'createdAt_desc']) ??
      'createdAt_desc',
    page: Number.isSafeInteger(page) && page > 0 ? page : 1,
    limit: [10, 25, 50, 100].includes(limit) ? limit : 25,
    search: params.get('search')?.trim().slice(0, 200) || undefined,
    role:
      pinned.role ?? oneOf(params.get('role'), userDirectoryRoles(population)),
    userStatus:
      oneOf(params.get('userStatus'), ['active', 'inactive', 'all']) ??
      'active',
    orgLevel: oneOf(params.get('orgLevel'), ORG_LEVELS),
    examLevel: oneOf(params.get('examLevel'), EXAM_LEVELS),
    missingExamLevel: params.get('missingExamLevel') === 'true' || undefined,
    classStatus: oneOf(params.get('classStatus'), ['assigned', 'unassigned']),
    classCodeId:
      population === 'regular' ? id(params.get('classCodeId')) : undefined,
    teacherStatus: oneOf(params.get('teacherStatus'), ['linked', 'unlinked']),
    cohortId:
      population === 'nonprofit'
        ? (pinned.cohortId ??
          (groupId ? undefined : id(params.get('cohortId'))))
        : undefined,
    organizationId:
      population === 'nonprofit'
        ? (pinned.organizationId ?? id(params.get('organizationId')))
        : undefined,
    groupId,
  };
}

export function directorySearchParams(
  filters: AdminUserDirectoryFilters,
  current = new URLSearchParams()
) {
  const next = new URLSearchParams(current);
  for (const key of Object.keys(
    parseUserDirectoryFilters(new URLSearchParams(), filters.population)
  )) {
    next.delete(key);
  }
  for (const [key, value] of Object.entries(filters)) {
    if (
      key !== 'population' &&
      value !== undefined &&
      value !== '' &&
      value !== false
    )
      next.set(key, String(value));
  }
  return next;
}

export const USER_DIRECTORY_PATH = '/admin/users';
export const ORGANIZATION_DIRECTORY_PATH = '/admin/nonprofit-organizations';
/** Tabs of the organization directory (`?view=`). */
export const ORGANIZATION_DIRECTORY_VIEWS = [
  'organizations',
  'people',
] as const;
/** Tabs of one organization record (`?tab=`). */
export const ORGANIZATION_RECORD_TABS = [
  'groups',
  'people',
  'onboarding',
  'settings',
] as const;
/** Tabs of one user record (`?tab=`). */
export const USER_RECORD_TABS = ['profile', 'access', 'relationships'] as const;

const ORGANIZATION_RECORD_PATH =
  /^\/admin\/nonprofit-organizations\/[a-f\d]{24}$/i;
/** The retired cohort pages: the directory and one cohort record. */
const RETIRED_COHORT_PATH = /^\/admin\/nonprofit-cohorts(\/[a-f\d]{24})?$/i;

/**
 * User records return to a directory, another user record, organization record,
 * or validated product adoption report, including on reload, where the value
 * arrives from the URL and is therefore untrusted. Anything
 * else (absolute, protocol-relative, backslashed, or an unknown path) collapses
 * to the user directory, and only whitelisted params are echoed through.
 */
export function validatedUserReturnTo(
  value: string | null | undefined,
  depth = 0
): string {
  if (
    !value ||
    depth > 3 ||
    value.length > 12000 ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\')
  )
    return USER_DIRECTORY_PATH;
  const [pathname, query = ''] = value.split('?');
  if (pathname === '/admin/product-analytics')
    return validatedProductAnalyticsReturnTo(value) ?? USER_DIRECTORY_PATH;
  const raw = new URLSearchParams(query);
  // Student links in a teacher's caseload return to that teacher. Rebuild a
  // bounded chain so nested records cannot preserve arbitrary return URLs.
  if (/^\/admin\/users\/[a-f\d]{24}$/i.test(pathname)) {
    const next = new URLSearchParams({
      tab: oneOf(raw.get('tab'), USER_RECORD_TABS) ?? 'profile',
    });
    if (raw.has('returnTo'))
      next.set(
        'returnTo',
        validatedUserReturnTo(raw.get('returnTo'), depth + 1)
      );
    return `${pathname}?${next}`;
  }
  // Links and bookmarks left over from the cohort pages land on the nonprofit
  // People view with their account filters intact.
  if (RETIRED_COHORT_PATH.test(pathname))
    return `${ORGANIZATION_DIRECTORY_PATH}?${directorySearchParams(
      parseUserDirectoryFilters(raw, 'nonprofit'),
      new URLSearchParams({ view: 'people' })
    ).toString()}`;
  const record = ORGANIZATION_RECORD_PATH.test(pathname);
  if (
    pathname !== USER_DIRECTORY_PATH &&
    pathname !== ORGANIZATION_DIRECTORY_PATH &&
    !record
  )
    return USER_DIRECTORY_PATH;
  const normalized = directorySearchParams(
    parseUserDirectoryFilters(
      raw,
      pathname === USER_DIRECTORY_PATH ? 'regular' : 'nonprofit'
    )
  );
  const view =
    pathname === ORGANIZATION_DIRECTORY_PATH
      ? oneOf(raw.get('view'), ORGANIZATION_DIRECTORY_VIEWS)
      : undefined;
  const tab = record
    ? oneOf(raw.get('tab'), ORGANIZATION_RECORD_TABS)
    : undefined;
  if (view) normalized.set('view', view);
  if (tab) normalized.set('tab', tab);
  if (record && raw.has('returnTo')) {
    // Only a directory can be a nested origin; never recursively accept detail URLs.
    const parent = raw.get('returnTo');
    if (parent?.split('?')[0] === ORGANIZATION_DIRECTORY_PATH)
      normalized.set('returnTo', validatedUserReturnTo(parent));
  }
  return `${pathname}?${normalized.toString()}`;
}

/**
 * The Back label for a user record, derived from its one validated origin so
 * the label, the href and the browser-pop destination cannot disagree. Pass the
 * output of `validatedUserReturnTo`, whose allowed shapes these prefixes cover.
 */
export const userReturnToLabel = (returnTo: string): string =>
  returnTo.startsWith('/admin/users/')
    ? 'Back to user record'
    : returnTo.startsWith('/admin/product-analytics')
      ? 'Back to Product Adoption'
      : returnTo.startsWith(`${ORGANIZATION_DIRECTORY_PATH}/`)
        ? 'Back to organization'
        : returnTo.startsWith(ORGANIZATION_DIRECTORY_PATH)
          ? 'Back to nonprofit people'
          : 'Back to Users';

/** One user record: `/admin/users/:userId`, with the id URL-encoded. */
const USER_RECORD_PATH = /^\/admin\/users\/[^/]+$/;

/**
 * The nav section a location belongs to, for the one path where that is not
 * the section it sits under. A user record is shared: opened from the user
 * directory it is a Users page, but opened from a nonprofit surface its Back
 * button returns there, so the drawer keeps Nonprofit Organizations lit rather
 * than moving the highlight to Users for a page the staff member never left
 * the nonprofit flow to reach. Derived from the same validated origin as the
 * Back label and href, so the three cannot disagree. Every other path is its
 * own section, which callers read from `undefined`.
 */
export const navSectionForLocation = (
  pathname: string,
  search?: string
): string | undefined => {
  if (!USER_RECORD_PATH.test(pathname)) return undefined;
  let returnTo = validatedUserReturnTo(
    new URLSearchParams(search).get('returnTo')
  );
  for (
    let depth = 0;
    depth < 4 && USER_RECORD_PATH.test(returnTo.split('?')[0]);
    depth++
  )
    returnTo = validatedUserReturnTo(
      new URLSearchParams(returnTo.split('?')[1]).get('returnTo')
    );
  if (returnTo.startsWith('/admin/product-analytics'))
    return '/admin/product-analytics';
  return returnTo.startsWith(ORGANIZATION_DIRECTORY_PATH)
    ? ORGANIZATION_DIRECTORY_PATH
    : USER_DIRECTORY_PATH;
};

export const userDetailHref = (userId: string, returnTo: string): string =>
  `/admin/users/${encodeURIComponent(userId)}?${new URLSearchParams({ returnTo: validatedUserReturnTo(returnTo) }).toString()}`;

/**
 * Link into an organization record's Levels & groups tab.
 *
 * Callers build the origin from an already-validated value. This checks for
 * escaping the app and encodes the query; `organizationReturnTo` validates
 * the destination's complete return context when the record reads it.
 */
export const organizationRecordHref = (
  organizationId: string,
  returnTo: string
): string =>
  `${ORGANIZATION_DIRECTORY_PATH}/${encodeURIComponent(organizationId)}?${new URLSearchParams(
    {
      tab: 'groups',
      returnTo:
        returnTo.startsWith('/') &&
        !returnTo.startsWith('//') &&
        !returnTo.includes('\\')
          ? returnTo
          : USER_DIRECTORY_PATH,
    }
  ).toString()}`;

export const adminUserLabel = (user: {
  displayName?: string | null;
  email?: string | null;
  id?: string;
  auth0Id?: string;
}) =>
  user.displayName ||
  user.email ||
  user.auth0Id ||
  user.id ||
  'Unnamed account';

export const adminRoleLabel = (role: string) =>
  role === 'user'
    ? 'Student'
    : role
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
